// ui/components/AttachmentPreviewModal.tsx
import React from "react";
import type { AttachmentMeta } from "../types/attachments";

interface AttachmentPreviewModalProps {
  open: boolean;
  attachment: AttachmentMeta | null;
  onClose: () => void;
}

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  open,
  attachment,
  onClose,
}) => {
  if (!open || !attachment) return null;

  const { name, url, contentType } = attachment;

  // 最小防呆：避免 url / name / contentType 為 undefined 時造成 includes 或 regex test 崩潰
  const safeName = (name ?? "未命名檔案").toString();
  const safeUrl = (url ?? "").toString();
  const ct = (contentType ?? "").toString().toLowerCase();
  const canOpen = safeUrl.trim().length > 0;

  const isImage =
    ct.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|svg)($|\?)/i.test(safeUrl);

  const isPdf = ct === "application/pdf" || /\.pdf($|\?)/i.test(safeUrl);

  const isWord = ct.includes("word") || /\.(docx?)($|\?)/i.test(safeUrl);

  const handleOpenInNewTab = () => {
    if (!canOpen) return;
    // 在新視窗/分頁開啟，讓瀏覽器自行預覽或下載
    window.open(safeUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="lop-modal-backdrop" onClick={onClose}>
      <div
        className="lop-modal lop-modal-large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lop-modal-header">
          <h3>預覽檔案：{safeName}</h3>
          <button
            type="button"
            onClick={onClose}
            className="lop-modal-close"
            aria-label="關閉預覽"
          >
            ×
          </button>
        </div>

        <div className="lop-modal-body">
          {isImage && canOpen ? (
            <div
              className="lop-preview-wrapper"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxHeight: "70vh",
              }}
            >
              <img
                src={safeUrl}
                alt={safeName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : (
            <div
              className="lop-preview-fallback"
              style={{
                minHeight: "40vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "1.5rem",
              }}
            >
              {!canOpen ? (
                <>
                  <p style={{ marginBottom: "0.5rem" }}>
                    此筆資料目前沒有可預覽的檔案連結。
                  </p>
                  <p style={{ marginBottom: "1rem" }}>
                    請確認是否已上傳附件，或附件 URL 欄位是否為空。
                  </p>
                </>
              ) : isPdf || isWord ? (
                <>
                  <p style={{ marginBottom: "0.5rem" }}>
                    此檔案類型（{isPdf ? "PDF" : "Word"}）在目前預覽環境下
                    可能無法內嵌顯示。
                  </p>
                  <p style={{ marginBottom: "1rem" }}>
                    請使用下方「在新視窗預覽」或「下載檔案」按鈕開啟。
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: "0.5rem" }}>
                    此檔案類型 ({contentType ?? "未知"}) 暫不支援線上預覽。
                  </p>
                  <p style={{ marginBottom: "1rem" }}>
                    仍可使用下方「下載檔案」按鈕取得原始檔案。
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div
          className="lop-modal-footer"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
            paddingTop: "0.75rem",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="lop-secondary-button"
          >
            關閉
          </button>

          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="lop-primary-button"
            disabled={!canOpen}
            aria-disabled={!canOpen}
            style={!canOpen ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
          >
            在新視窗預覽
          </button>

          <a
            href={canOpen ? safeUrl : "#"}
            download={safeName}
            target="_blank"
            rel="noreferrer"
            className="lop-outline-button"
            onClick={(e) => {
              if (!canOpen) e.preventDefault();
            }}
            style={!canOpen ? { opacity: 0.6, pointerEvents: "none" } : undefined}
            aria-disabled={!canOpen}
          >
            下載檔案
          </a>
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;
